import { useMemo } from 'react';

import { useDataProvider } from '@work4all/data';

import { ObjectTypeByEntity } from '@work4all/models';
import { Event } from '@work4all/models/lib/Classes/Event.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { useCommentContext } from '@work4all/utils';

import { inlineMentions } from '../../../input/format-text/TextEditor/plugins/mention/utils';

import { useAddCommentMutation } from './use-add-comment-mutation';

/**
 * Wrapper for comments to query & upsert
 * @param entity
 * @param id
 */
export const useHistoryEvents = (
  entity: Entities,
  id: string | number,
  skip?: boolean
) => {
  const { comment, setComment } = useCommentContext();
  const requestData: DataRequest = useMemo(() => {
    const data: Event<EMode.query> = {
      createEvent: {
        id: null,
        creationDate: null,
        user: { id: null, displayName: null },
      },
      changeEvent: {
        id: null,
        creationDate: null,
        changes: [{ propertyName: null, newValue: null }],
        user: { id: null, displayName: null },
      },
      commentEvent: {
        id: null,
        creationDate: null,
        text: null,
        user: { id: null, displayName: null },
        objectType: null,
        parentObjectType: null,
      },
    };

    return {
      entity: Entities.event,
      data,
      vars: {
        objectType: ObjectTypeByEntity[entity],
        objectId: String(id),
      },
      skip,
    };
  }, [entity, id, skip]);

  const skipIfNoId = !id;
  const events = useDataProvider<Event>(requestData, skipIfNoId);

  const [addComment] = useAddCommentMutation({
    onCompleted() {
      events.refetch();
      setComment('');
    },
  });

  const handleAddComment = async (comment: string) => {
    if (!comment) return;
    await addComment({
      variables: {
        objectType: ObjectTypeByEntity[entity],
        codeOrId: String(id),
        comment: inlineMentions(comment),
      },
    });
  };

  return { events, addComment: handleAddComment, setComment, comment };
};
